"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  LOCALES,
  LOCALE_LABELS,
  LOCALE_FLAGS,
  EN_FLAG,
  LOCALIZED_PATHS,
  PRICING_ONLY_LOCALES,
  PRICING_ONLY_LOCALE_FLAGS,
  PRICING_ONLY_LOCALE_LABELS,
  hasPricingOnlyLocale,
  localizedHref,
  writePreferredLocale,
  type Locale,
  type PricingOnlyLocale,
} from "@/lib/i18n/locales";

function stripLocalePrefix(pathname: string): string {
  for (const locale of [...LOCALES, ...PRICING_ONLY_LOCALES]) {
    if (pathname === `/${locale}`) return "/";
    if (pathname.startsWith(`/${locale}/`)) return pathname.slice(locale.length + 1);
  }
  return pathname;
}

function localeFlag(locale: Locale | PricingOnlyLocale): string {
  return hasPricingOnlyLocale(locale)
    ? PRICING_ONLY_LOCALE_FLAGS[locale]
    : LOCALE_FLAGS[locale];
}

function localeLabel(locale: Locale | PricingOnlyLocale): string {
  return hasPricingOnlyLocale(locale)
    ? PRICING_ONLY_LOCALE_LABELS[locale]
    : LOCALE_LABELS[locale];
}

export function LocaleSwitcher({
  currentLocale,
}: {
  currentLocale?: Locale | PricingOnlyLocale;
}) {
  const pathname = usePathname() || "/";
  const baseEnglishPath = stripLocalePrefix(pathname);
  const currentFlag = currentLocale ? localeFlag(currentLocale) : EN_FLAG;
  const currentLabel = currentLocale ? localeLabel(currentLocale) : "EN";
  // Switching to English: keep current path. Switching to a locale: use
  // localizedHref so blog/niche paths fall back to locale homepage instead
  // of 404'ing on untranslated routes.
  const englishHref = baseEnglishPath;

  return (
    <details className="relative">
      <summary className="flex min-h-11 cursor-pointer list-none items-center gap-1.5 rounded-md border border-[#2A2825] bg-[#1F1C18] px-3 py-2 text-sm text-[#C7BFB0] transition hover:border-[#F59E0B] hover:text-[#F59E0B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F59E0B] focus-visible:ring-offset-2 focus-visible:ring-offset-[#121110]">
        <span aria-hidden="true">{currentFlag}</span>
        <span>{currentLabel}</span>
        <span aria-hidden="true">&darr;</span>
      </summary>
      <ul className="absolute right-0 z-50 mt-2 max-h-[calc(100dvh-5rem)] w-52 overflow-y-auto rounded-md border border-[#2A2825] bg-[#1F1C18] py-2 shadow-lg">
        <li>
          <Link
            href={englishHref}
            onClick={() => writePreferredLocale(null)}
            className="flex min-h-11 items-center gap-3 px-4 py-2 text-sm text-[#C7BFB0] hover:bg-[#2A2825] hover:text-[#F59E0B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#F59E0B]"
          >
            <span aria-hidden="true" className="text-base">{EN_FLAG}</span>
            <span>English</span>
          </Link>
        </li>
        {LOCALES.map((locale) => (
          <li key={locale}>
            <Link
              href={localizedHref(locale, baseEnglishPath)}
              onClick={() => writePreferredLocale(locale)}
              className="flex min-h-11 items-center gap-3 px-4 py-2 text-sm text-[#C7BFB0] hover:bg-[#2A2825] hover:text-[#F59E0B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#F59E0B]"
            >
              <span aria-hidden="true" className="text-base">{LOCALE_FLAGS[locale]}</span>
              <span>{LOCALE_LABELS[locale]}</span>
            </Link>
          </li>
        ))}
        {baseEnglishPath === "/pricing" && PRICING_ONLY_LOCALES.map((locale) => (
          <li key={locale}>
            <Link
              href={`/${locale}/pricing`}
              className="flex min-h-11 items-center gap-3 px-4 py-2 text-sm text-[#C7BFB0] hover:bg-[#2A2825] hover:text-[#F59E0B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#F59E0B]"
            >
              <span aria-hidden="true" className="text-base">{PRICING_ONLY_LOCALE_FLAGS[locale]}</span>
              <span>{PRICING_ONLY_LOCALE_LABELS[locale]}</span>
            </Link>
          </li>
        ))}
      </ul>
      {!LOCALIZED_PATHS.has(baseEnglishPath) && (
        <span className="sr-only">Locale switching from this page returns to the locale homepage.</span>
      )}
    </details>
  );
}
